import React, { useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useLanguage } from '../../contexts/LanguageContext'
import {
    getEventAttendance,
    getEventGateCounts,
    getMerchantEvents,
    validateMerchantTicket,
} from '../../utils/merchantToolStorage'

const gates = ['Gate A', 'Gate B', 'Gate C', 'Gate D']

export default function MerchantScanTicket() {
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const events = useMemo(() => getMerchantEvents(), [])
    const [eventId, setEventId] = useState(events[0]?.id || '')
    const [gateName, setGateName] = useState('Gate A')
    const [staffName, setStaffName] = useState('')
    const [staffCode, setStaffCode] = useState('')
    const [ticketCode, setTicketCode] = useState('')
    const [result, setResult] = useState(null)
    const [, setRefreshKey] = useState(0)

    const event = useMemo(
        () => events.find((item) => item.id === eventId) || null,
        [eventId, events]
    )

    const gateCounts = event ? getEventGateCounts(event.id) : {}
    const attendance = event ? getEventAttendance(event.id) : []

    const handleValidate = () => {
        if (!event) {
            setResult({
                tone: 'warning',
                title: isFr ? 'Choisissez un evenement' : 'Choose an event',
                body: isFr
                    ? 'Creez ou choisissez un evenement avant de scanner.'
                    : 'Create or choose an event before scanning.',
            })
            return
        }

        if (!staffName.trim() || staffCode.trim().length < 4) {
            setResult({
                tone: 'warning',
                title: isFr ? 'Acces personnel requis' : 'Staff access required',
                body: isFr
                    ? 'Ajoutez le nom du personnel et un code scanner a 4 chiffres.'
                    : 'Enter the staff name and a 4 digit scanner code.',
            })
            return
        }

        const validation = validateMerchantTicket({
            eventId: event.id,
            gateName,
            ticketCode,
            staffName,
        })

        setResult({
            ...validation,
            title: isFr && validation.tone === 'success'
                ? 'Billet accepte'
                : isFr && validation.tone === 'error'
                    ? 'Billet deja scanne'
                    : validation.title,
            body: validation.body,
        })
        if (validation.tone === 'success') {
            setTicketCode('')
            setRefreshKey((value) => value + 1)
        }
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Validation billets' : 'Ticket validation'}
            title={isFr ? 'Scanner billet' : 'Scan Ticket'}
            subtitle={
                isFr
                    ? 'Choisissez l evenement, la porte et le personnel. Cette vue reste limitee a la validation des billets.'
                    : 'Choose the event, gate, and staff. This view stays focused on ticket validation only.'
            }
            chips={[
                isFr ? 'Controle entree' : 'Gate control',
                event?.country || (isFr ? 'Pays evenement' : 'Event country'),
                isFr ? 'Mode scanner' : 'Scanner mode',
            ]}
            primaryAction={{
                label: isFr ? 'Valider billet' : 'Validate Ticket',
                onClick: handleValidate,
            }}
            left={
                <Stack spacing={2}>
                    <TextField
                        select
                        label={isFr ? 'Evenement' : 'Event'}
                        value={eventId}
                        onChange={(e) => {
                            setEventId(e.target.value)
                            setResult(null)
                        }}
                    >
                        {events.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.title} {item.country ? `- ${item.country}` : ''}
                            </MenuItem>
                        ))}
                        {events.length === 0 && (
                            <MenuItem value="" disabled>
                                {isFr ? 'Aucun evenement cree' : 'No events created'}
                            </MenuItem>
                        )}
                    </TextField>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {gates.map((gate) => {
                            const selected = gate === gateName
                            return (
                                <Chip
                                    key={gate}
                                    label={`${gate} ${gateCounts[gate] || 0}`}
                                    onClick={() => setGateName(gate)}
                                    sx={{
                                        bgcolor: selected ? '#FF8A00' : 'rgba(91,46,255,0.22)',
                                        color: selected ? '#0B0620' : '#FFFFFF',
                                        fontWeight: 900,
                                        border: selected ? '1px solid #FF8A00' : '1px solid rgba(183,166,255,0.16)',
                                    }}
                                />
                            )
                        })}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                            fullWidth
                            label={isFr ? 'Nom personnel porte' : 'Gate staff name'}
                            value={staffName}
                            onChange={(e) => setStaffName(e.target.value)}
                            placeholder="Gate A staff 1"
                        />
                        <TextField
                            fullWidth
                            label={isFr ? 'Code scanner' : 'Scanner code'}
                            value={staffCode}
                            onChange={(e) => setStaffCode(e.target.value)}
                            placeholder="4 digit code"
                            type="password"
                        />
                    </Stack>

                    <TextField
                        label={isFr ? 'Code billet ou reference' : 'Ticket code or reference'}
                        value={ticketCode}
                        onChange={(e) => setTicketCode(e.target.value)}
                        placeholder={isFr ? 'Ex: TKT-2026-001' : 'Ex: TKT-2026-001'}
                    />

                    {result && (
                        <Alert severity={result.tone}>
                            <strong>{result.title}</strong>
                            <br />
                            {result.body}
                        </Alert>
                    )}

                    <Button
                        variant="outlined"
                        startIcon={<QrCodeScannerRoundedIcon />}
                        onClick={handleValidate}
                        sx={{
                            color: '#FFFFFF',
                            borderColor: 'rgba(183,166,255,0.34)',
                            borderRadius: 4,
                            textTransform: 'none',
                            fontWeight: 900,
                        }}
                    >
                        {isFr ? 'Scanner / valider' : 'Scan / Validate'}
                    </Button>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <QrCodeScannerRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Mode scanner seul' : 'Scanner-only mode'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr
                                        ? 'Le personnel valide uniquement les billets pour cet evenement et cette porte.'
                                        : 'Staff only validate tickets for this event and selected gate.'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <VerifiedRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Porte active' : 'Active gate'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {event ? `${gateName} - ${gateCounts[gateName] || 0} checked` : (isFr ? 'Aucun evenement' : 'No event')}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <BadgeRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Personnel' : 'Staff'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {staffName.trim() || (isFr ? 'Aucun personnel' : 'No staff assigned')}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <ErrorOutlineRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Total entree' : 'Total checked'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {attendance.length} {isFr ? 'billets valides' : 'tickets validated'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
            }
        />
    )
}
